'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { isSemesterId } from '../lib/semesters';
import { getPhysicalWrite, subscribePhysicalWrite, SCHEDULE_WRITE_SCOPE } from '../hooks/physicalWriteSlot';
import { reconstructOwnedSchedule, type OwnedScheduleProjection } from './reconstructOwnedSchedule';
import type { RutgersCourse, Schedule, ScheduledCourse } from '../types/interfaces';

type Source = 'initial' | 'assistant' | 'semester' | 'review';
export type HydrationPhase = 'ready' | 'pending' | 'committing' | 'conflict' | 'error';
export interface HydrationTicket { readonly id: symbol }
export interface OwnedHydrationSnapshot { record: Schedule; catalog: RutgersCourse[]; projection: OwnedScheduleProjection }
interface Options {
  ownerId: string | null; scheduleId: string | null; routeKey: string; visibleTerm: string;
  enabled: boolean; hasWorkspace: boolean;
  readSchedule: (id: string) => Promise<Schedule>; readProfile: (owner: string) => Promise<unknown>;
  readCatalog: (term: string) => Promise<RutgersCourse[]>;
  mapSection: (course: RutgersCourse) => ScheduledCourse;
  publish: (snapshot: OwnedHydrationSnapshot, isCurrent: () => boolean) => void;
  onLoading: (loading: boolean, isCurrent: () => boolean) => void;
  onForeign: (record: Schedule, isCurrent: () => boolean) => void;
}
interface Receipt {
  ticket: HydrationTicket; context: object; epoch: symbol; revision: number; source: Source;
  targetTerm: string | null; started: boolean; stopWait: (() => void) | null;
}
interface State { phase: HydrationPhase; targetTerm: string | null; error: string | null; acceptedCommitRevision: number; ticket: HydrationTicket | null }

/** One read continuation owns its original edit receipt through profile/catalog waits.
 * Physical writes are observed, never acquired/cancelled/released by this reader.
 */
export function useOwnedScheduleHydration(options: Options) {
  const { ownerId, scheduleId, routeKey, enabled, visibleTerm } = options;
  const [state, setState] = useState<State>({ phase: 'ready', targetTerm: null, error: null, acceptedCommitRevision: 0, ticket: null });
  const context = useMemo(() => ({ ownerId, scheduleId, routeKey, enabled }), [ownerId, scheduleId, routeKey, enabled]);
  const canEditAutosaveContext = state.phase === 'ready';
  const editContext = useMemo(() => ({ context, visibleTerm, commit: state.acceptedCommitRevision, canEditAutosaveContext }),
    [context, visibleTerm, state.acceptedCommitRevision, canEditAutosaveContext]);
  const committed = useRef<{ context: typeof context; editContext: typeof editContext; options: Options } | null>(null);
  const epoch = useRef<symbol | null>(null), editRevision = useRef(0), active = useRef<Receipt | null>(null);
  const latestTicket = useRef<HydrationTicket | null>(null);
  const phase = useRef<HydrationPhase>('ready');
  const previousDestination = useRef<{ ownerId: string | null; scheduleId: string | null } | null>(null);

  useLayoutEffect(() => { committed.current = { context, editContext, options }; });
  useLayoutEffect(() => {
    const lifetime = Symbol('owned-hydration'); epoch.current = lifetime;
    const sameDestination = previousDestination.current?.ownerId === ownerId && previousDestination.current.scheduleId === scheduleId;
    const retainedConflict = sameDestination && phase.current !== 'ready';
    previousDestination.current = { ownerId, scheduleId };
    phase.current = retainedConflict ? 'conflict' : 'ready'; editRevision.current = 0;
    if (context.ownerId && context.scheduleId && enabled && !options.hasWorkspace) options.onLoading(!retainedConflict,
      () => epoch.current === lifetime && committed.current?.context === context);
    setState(previous => epoch.current === lifetime && committed.current?.context === context
      ? { ...previous, phase: retainedConflict ? 'conflict' : 'ready', targetTerm: retainedConflict ? previous.targetTerm : null, error: null, ticket: null } : previous);
    return () => {
      if (epoch.current === lifetime) { epoch.current = null; latestTicket.current = null; active.current?.stopWait?.(); active.current = null; }
    };
  }, [context]);

  const ownsOutcome = useCallback((receipt: Receipt) => epoch.current === receipt.epoch
    && committed.current?.context === receipt.context && committed.current.options.enabled
    && latestTicket.current === receipt.ticket && editRevision.current === receipt.revision, []);
  const current = useCallback((receipt: Receipt) => ownsOutcome(receipt) && active.current === receipt, [ownsOutcome]);
  const begin = useCallback((source: Source, targetTerm: string | null = null): Receipt | null => {
    const c = committed.current, e = epoch.current;
    if (!c || !e || c.context !== context || !c.options.enabled || !context.ownerId || !context.scheduleId) return null;
    active.current?.stopWait?.();
    const receipt: Receipt = { ticket: { id: Symbol('hydration-intent') }, context, epoch: e, revision: editRevision.current,
      source, targetTerm, started: false, stopWait: null };
    active.current = receipt; latestTicket.current = receipt.ticket; phase.current = 'pending';
    setState(previous => ownsOutcome(receipt) ? { ...previous, phase: 'pending', targetTerm, error: null, ticket: receipt.ticket } : previous);
    c.options.onLoading(!c.options.hasWorkspace, () => ownsOutcome(receipt));
    return receipt;
  }, [context, ownsOutcome]);
  const fail = useCallback((receipt: Receipt, message: string) => {
    if (!current(receipt)) return;
    receipt.stopWait?.(); active.current = null; phase.current = 'error';
    setState(previous => ownsOutcome(receipt) ? { ...previous, phase: 'error', targetTerm: receipt.targetTerm, error: message, ticket: null } : previous);
    committed.current!.options.onLoading(false, () => ownsOutcome(receipt));
  }, [current, ownsOutcome]);
  const waitForWrites = useCallback((receipt: Receipt): Promise<boolean> => {
    if (!current(receipt)) return Promise.resolve(false);
    if (getPhysicalWrite(SCHEDULE_WRITE_SCOPE) === undefined) return Promise.resolve(true);
    return new Promise(resolve => {
      let done = false, unsubscribe = () => {};
      const finish = (valid: boolean) => { if (done) return; done = true; unsubscribe(); receipt.stopWait = null; resolve(valid); };
      receipt.stopWait = () => finish(false);
      const check = () => { if (!current(receipt)) finish(false); else if (getPhysicalWrite(SCHEDULE_WRITE_SCOPE) === undefined) finish(true); };
      unsubscribe = subscribePhysicalWrite(SCHEDULE_WRITE_SCOPE, check); check();
    });
  }, [current]);
  const run = useCallback(async (receipt: Receipt): Promise<boolean> => {
    if (!current(receipt) || receipt.started) return false;
    receipt.started = true;
    try {
      // The original revision is deliberately not refreshed after this wait.
      // A previously retained force callback may acquire its slot in the same
      // event before its last-dispatch refusal settles. Recheck synchronously
      // before issuing the read; every occupied interval waits on its release
      // subscription, never a timer or a polling read.
      do {
        if (!await waitForWrites(receipt) || !current(receipt)) return false;
      } while (getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined);
      const captured = committed.current!;
      const response = await captured.options.readSchedule(context.scheduleId!);
      if (!current(receipt)) return false;
      const row = JSON.parse(JSON.stringify(response)) as Schedule;
      const profile = await captured.options.readProfile(context.ownerId!);
      if (!current(receipt)) return false;
      if (!profile || typeof profile !== 'object' || !('uid' in profile) || profile.uid !== context.ownerId) throw new Error('Your profile could not be loaded.');
      if (!row || row.schedule_id !== context.scheduleId) throw new Error('The saved schedule could not be verified.');
      if (row.user_id !== context.ownerId) {
        captured.options.onForeign(row, () => current(receipt));
        fail(receipt, row.is_public ? 'Opening the public schedule.' : 'You do not have permission to view this schedule.');
        return false;
      }
      if (!isSemesterId(row.term_id) || (receipt.targetTerm !== null && receipt.targetTerm !== row.term_id)) throw new Error('The saved schedule term could not be verified.');
      // Detach returned mutable API data; no row/catalog is visible until commit.
      const record = row;
      const term = row.term_id; receipt.targetTerm = term;
      setState(previous => current(receipt) ? { ...previous, targetTerm: term } : previous);
      const catalog = await captured.options.readCatalog(term);
      if (!current(receipt)) return false;
      if (!Array.isArray(catalog) || catalog.length === 0) throw new Error('The course catalog could not be loaded.');
      const ownedCatalog = JSON.parse(JSON.stringify(catalog)) as RutgersCourse[];
      const snapshot = { record, catalog: ownedCatalog, projection: reconstructOwnedSchedule(record, ownedCatalog, captured.options.mapSection) };
      if (!current(receipt) || getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined) {
        if (current(receipt)) fail(receipt, 'Saving started before loading completed. Review the saved version to continue.');
        return false;
      }
      phase.current = 'committing';
      // Async completion, outside render/effect: one immediate local commit only.
      flushSync(() => {
        if (!current(receipt)) return;
        committed.current!.options.publish(snapshot, () => current(receipt));
        setState(previous => current(receipt) ? { phase: 'committing', targetTerm: term, error: null,
          acceptedCommitRevision: previous.acceptedCommitRevision + 1, ticket: receipt.ticket } : previous);
        committed.current!.options.onLoading(false, () => ownsOutcome(receipt));
      });
      return true;
    } catch (error) {
      fail(receipt, error instanceof Error ? error.message : 'The saved schedule could not be loaded.');
      return false;
    }
  }, [context, current, fail, waitForWrites, ownsOutcome]);
  // Consumption is after the committed projection, not between queued setters.
  useLayoutEffect(() => {
    const receipt = active.current;
    if (state.phase !== 'committing' || !receipt || state.ticket !== receipt.ticket || !current(receipt)) return;
    active.current = null; phase.current = 'ready';
    setState(previous => ownsOutcome(receipt) ? { ...previous, phase: 'ready', ticket: null } : previous);
  }, [state, current, ownsOutcome]);
  const markEdit = useCallback(() => {
    const c = committed.current;
    if (!epoch.current || !c || c.context !== context || c.editContext !== editContext || !c.options.enabled || !context.ownerId || !context.scheduleId) return false;
    editRevision.current++;
    const receipt = active.current;
    if (receipt) {
      receipt.stopWait?.(); active.current = null; phase.current = 'conflict';
      const editorCurrent = () => epoch.current !== null && committed.current?.context === context && committed.current.editContext === editContext;
      setState(previous => editorCurrent() ? { ...previous, phase: 'conflict', targetTerm: receipt.targetTerm, error: null, ticket: null } : previous);
      c.options.onLoading(false, editorCurrent);
    }
    return true;
  }, [context, editContext]);
  const canWrite = useCallback(() => epoch.current !== null && committed.current?.context === context
    && committed.current.options.enabled && phase.current === 'ready' && committed.current.options.hasWorkspace, [context]);
  const load = useCallback(async (source: Exclude<Source, 'semester'> = 'initial', requestedId: unknown = context.scheduleId) => {
    if (requestedId !== context.scheduleId || (source === 'assistant' && !committed.current?.options.hasWorkspace)) return false;
    const receipt = begin(source); return receipt ? run(receipt) : false;
  }, [context, begin, run]);
  const reserveTerm = useCallback((term: string): HydrationTicket | null => {
    if (!canWrite() || !isSemesterId(term)) return null;
    return begin('semester', term)?.ticket ?? null;
  }, [canWrite, begin]);
  const resumeTerm = useCallback((ticket: HydrationTicket) => {
    const receipt = active.current;
    return receipt?.ticket === ticket ? run(receipt) : Promise.resolve(false);
  }, [run]);
  const rejectTerm = useCallback((ticket: HydrationTicket) => {
    const receipt = active.current;
    if (receipt?.ticket === ticket) fail(receipt, 'The semester change could not be completed. Review the saved version to continue.');
  }, [fail]);
  return { ...state, load, markEdit, canWrite, reserveTerm, resumeTerm, rejectTerm };
}
